/**
 * DB Overall Ranking Page
 *
 * Progressive enhancement:
 * - If "Load more" is enabled in the section, this script fetches the next paginated page
 *   and appends the next set of rendered list items.
 * - The initial HTML is rendered server-side (Liquid) for SEO.
 *
 * Image fallback (optional):
 * - If an <img> has data-fallback-src, we swap to that URL if the primary src errors.
 */

(() => {
  const roots = document.querySelectorAll('[data-dbor-section]');

  const escapeCss = (value) => {
    if (window.CSS && typeof window.CSS.escape === 'function') return window.CSS.escape(value);
    // Minimal fallback: escape quotes and backslashes
    return String(value).replace(/\\/g, '\\\\').replace(/"/g, '\\"');
  };

  const applyImageFallback = (img) => {
    if (!img) return;
    const fallback = img.getAttribute('data-fallback-src');
    if (!fallback) return;
    if (img.dataset.dborFallbackApplied === '1') return;

    // Avoid loops if fallback also fails
    img.dataset.dborFallbackApplied = '1';
    if (img.src !== fallback) img.src = fallback;
  };

  const bindImageFallback = (img) => {
    if (!img) return;
    const fallback = img.getAttribute('data-fallback-src');
    if (!fallback) return;
    if (img.dataset.dborFallbackBound === '1') return;

    img.dataset.dborFallbackBound = '1';

    img.addEventListener('error', () => applyImageFallback(img), { once: true });

    // If the image already failed before we bound the listener:
    if (img.complete && img.naturalWidth === 0) {
      applyImageFallback(img);
    }
  };

  const setupImageFallbacks = (scope) => {
    if (!scope) return;
    scope.querySelectorAll('img[data-fallback-src]').forEach(bindImageFallback);
  };

  roots.forEach((root) => {
    // Always bind image fallbacks (works whether Load more is enabled or not)
    setupImageFallbacks(root);

    const loadMoreBtn = root.querySelector('[data-dbor-load-more]');
    if (!loadMoreBtn) return;

    const list = root.querySelector('[data-dbor-list]');
    const paginationWrap = root.querySelector('[data-dbor-pagination]');
    const statusEl = root.querySelector('[data-dbor-status]');

    if (!list) return;

    // If the button is present, the merchant likely enabled Load More.
    // Keep the pagination in the HTML for SEO, but hide it for users when JS is available.
    if (paginationWrap) paginationWrap.style.display = 'none';
    loadMoreBtn.hidden = false;

    const setStatus = (msg) => {
      if (!statusEl) return;
      statusEl.textContent = msg || '';
    };

    const getNextUrl = () => loadMoreBtn.getAttribute('data-next-url');

    const setNextUrl = (url) => {
      if (url) loadMoreBtn.setAttribute('data-next-url', url);
      else loadMoreBtn.removeAttribute('data-next-url');
    };

    const sectionId = root.getAttribute('data-section-id');

    loadMoreBtn.addEventListener('click', async () => {
      const nextUrl = getNextUrl();
      if (!nextUrl) return;

      loadMoreBtn.disabled = true;
      setStatus('Loading…');

      try {
        const res = await fetch(nextUrl, {
          credentials: 'same-origin',
          headers: { 'X-Requested-With': 'XMLHttpRequest' }
        });

        if (!res.ok) throw new Error(`Request failed (${res.status})`);

        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');

        let nextRoot = null;
        if (sectionId) {
          nextRoot = doc.querySelector(
            `[data-dbor-section][data-section-id="${escapeCss(sectionId)}"]`
          );
        }
        if (!nextRoot) {
          nextRoot = doc.querySelector('[data-dbor-section]');
        }
        if (!nextRoot) throw new Error('Could not find the next page section markup');

        const nextList = nextRoot.querySelector('[data-dbor-list]');
        if (!nextList) throw new Error('Could not find list on next page');

        const items = Array.from(nextList.children);
        if (items.length === 0) throw new Error('No items found on next page');

        items.forEach((item) => {
          list.appendChild(item);
          // Bind fallbacks for any newly added images
          setupImageFallbacks(item);
        });

        const nextBtn = nextRoot.querySelector('[data-dbor-load-more]');
        const newNextUrl = nextBtn ? nextBtn.getAttribute('data-next-url') : null;

        if (newNextUrl) {
          setNextUrl(newNextUrl);
          loadMoreBtn.disabled = false;
          setStatus('');
        } else {
          // No more pages.
          setNextUrl(null);
          loadMoreBtn.disabled = true;
          loadMoreBtn.hidden = true;
          setStatus('All breeds loaded.');
        }
      } catch (err) {
        console.error('[DB Overall Ranking] Load more failed:', err);
        loadMoreBtn.disabled = false;
        setStatus('Could not load more. Please refresh or use normal pagination.');
        if (paginationWrap) paginationWrap.style.display = '';
      }
    });
  });
})();
